import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { NeevCloudMark } from '../components/Brand';
import { EyeIcon, EyeOffIcon, LockIcon, Spinner, ThemeToggle } from '../components/primitives';
import { useAuth } from '../context/AuthContext';
import { ApiError } from '../lib/api';
import { cx } from '../lib/format';

type Mode = 'login' | 'signup';

// Must match ORG_EMAIL_DOMAINS in server/.env.example — kept as a plain array
// here rather than fetched from the API, matching how products/categories are
// already hardcoded client-side (see lib/products.ts). Only enforced on
// registration; an already-existing account can sign in regardless.
const ORG_DOMAINS = ['neevcloud.com', 'rackbank.com', 'neevcloud.in', 'rackbank.in'];
const PRIMARY_DOMAIN = ORG_DOMAINS[0];

/**
 * The brand panel's pitch, one slide at a time — each grounded in something
 * the product actually does (nothing aspirational that isn't shipped). `lead`
 * is plain white; `accent` is the one phrase set in italic brand-teal.
 */
const SLIDES = [
  {
    lead: 'Every idea has a ',
    accent: 'home',
    body: "Every good product starts as somebody's observation. Capture bugs, customer feedback, and ideas the moment they happen — instead of losing them in a chat scroll.",
  },
  {
    lead: 'Nothing falls through the ',
    accent: 'cracks',
    body: 'Impact-ranked and status-tracked from New to Resolved, so the board always shows what actually needs attention next.',
  },
  {
    lead: 'One board, every ',
    accent: 'product',
    body: 'CPU NeevCloud and Pulse NeevCloud, filtered by team, category, or urgency — watch feedback get owned and resolved in the open.',
  },
];

export function LoginPage() {
  const { login, signup } = useAuth();

  const [mode, setMode] = useState<Mode>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setFieldErrors({});

    const trimmedEmail = email.trim().toLowerCase();
    const local: Record<string, string> = {};

    if (mode === 'signup' && name.trim().length < 2) {
      local.name = 'Please enter your full name.';
    }
    if (!/^\S+@\S+\.\S+$/.test(trimmedEmail)) {
      local.email = 'Enter a valid email address.';
    } else if (mode === 'signup') {
      const domain = trimmedEmail.split('@')[1];
      if (!ORG_DOMAINS.includes(domain)) {
        local.email = `Registration is only open to ${ORG_DOMAINS.join(', ')} addresses.`;
      }
    }
    if (mode === 'signup' && password.length < 8) {
      local.password = 'Password must be at least 8 characters.';
    } else if (mode === 'login' && password.length < 1) {
      local.password = 'Enter your password.';
    }
    if (Object.keys(local).length > 0) {
      setFieldErrors(local);
      return;
    }

    setSubmitting(true);
    try {
      if (mode === 'login') {
        await login(trimmedEmail, password);
      } else {
        await signup(name.trim(), trimmedEmail, password);
      }
      // On success the app swaps to the dashboard behind a brand transition —
      // no navigation needed here.
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        if (err.fields) setFieldErrors(err.fields);
      } else {
        setError('Something went wrong. Please try again.');
      }
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-dvh flex-col lg:p-6">
      <div className="relative flex flex-1 flex-col overflow-hidden lg:grid lg:min-h-[calc(100dvh-3rem)] lg:grid-cols-2 lg:rounded-[2rem] lg:shadow-2xl">
        <BrandPanel />

        {/* ---------------------------------------------------------------- form */}
        <section className="relative flex flex-1 items-center justify-center overflow-hidden px-5 py-10 sm:px-10">
          <ThemeToggle className="absolute top-5 right-5 z-10" />
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute -top-40 right-1/4 h-[40rem] w-[40rem] rounded-full bg-orchid-500/10 blur-[140px]" />
            <div className="absolute -bottom-52 left-1/4 h-[32rem] w-[32rem] rounded-full bg-lotus-500/8 blur-[130px]" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-sm"
          >
            <div className="surface-raised w-full p-7 sm:p-8">
              <div className="flex flex-col items-center text-center">
                <NeevCloudMark size={32} />
                <h1 className="mt-3 text-h2 font-bold text-mist-50 text-balance">
                  {mode === 'login' ? (
                    <>
                      Welcome to{' '}
                      <em className="not-italic" style={{ color: BRAND_TEAL }}>
                        Suggestion Box
                      </em>
                    </>
                  ) : (
                    <>
                      Join{' '}
                      <em className="not-italic" style={{ color: BRAND_TEAL }}>
                        Suggestion Box
                      </em>
                    </>
                  )}
                </h1>
                <p className="mt-1.5 text-sm text-mist-400">
                  {mode === 'login' ? 'Sign in to continue.' : 'Join your team on NeevCloud.'}
                </p>
              </div>

              <div className="hairline mt-6 mb-6 flex rounded-xl border bg-ink-900/60 p-0.5">
                {(['login', 'signup'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => {
                      setMode(m);
                      setError(null);
                      setFieldErrors({});
                    }}
                    aria-pressed={mode === m}
                    className={cx(
                      'flex-1 rounded-[0.625rem] px-4 py-2 text-sm font-semibold transition-colors duration-150',
                      mode === m
                        ? 'bg-[var(--tint-bg-strong)] text-mist-50'
                        : 'text-mist-500 hover:text-mist-200',
                    )}
                  >
                    {m === 'login' ? 'Sign in' : 'Create account'}
                  </button>
                ))}
              </div>

              <form onSubmit={submit} noValidate className="space-y-4">
                {error && (
                  <div
                    role="alert"
                    className="rounded-xl border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200"
                  >
                    {error}
                  </div>
                )}

                {mode === 'signup' && (
                  <Field
                    id="name"
                    label="Full name"
                    value={name}
                    onChange={setName}
                    placeholder="Priya Nair"
                    autoComplete="name"
                    error={fieldErrors.name}
                  />
                )}

                <Field
                  id="email"
                  label={mode === 'signup' ? 'Organization email' : 'Email'}
                  type="email"
                  value={email}
                  onChange={setEmail}
                  placeholder={`you@${PRIMARY_DOMAIN}`}
                  autoComplete="email"
                  error={fieldErrors.email}
                />

                <div>
                  <label htmlFor="password" className="label">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={mode === 'signup' ? 'At least 8 characters' : '••••••••'}
                      autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                      aria-invalid={Boolean(fieldErrors.password)}
                      aria-describedby={fieldErrors.password ? 'password-err' : undefined}
                      className="field pr-11"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute top-1/2 right-1 -translate-y-1/2 rounded-lg p-2 text-mist-500 transition-colors hover:text-mist-200"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeOffIcon className="h-4 w-4" />
                      ) : (
                        <EyeIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {fieldErrors.password && (
                    <p id="password-err" className="mt-1.5 text-xs text-rose-300">
                      {fieldErrors.password}
                    </p>
                  )}
                </div>

                {mode === 'signup' && (
                  <p className="text-xs leading-relaxed text-mist-600">
                    Registration is only open to{' '}
                    {ORG_DOMAINS.map((d, i) => (
                      <span key={d}>
                        {i > 0 && (i === ORG_DOMAINS.length - 1 ? ' or ' : ', ')}
                        <code className="rounded bg-[var(--tint-bg-strong)] px-1 py-0.5 text-mist-400">
                          @{d}
                        </code>
                      </span>
                    ))}{' '}
                    addresses.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn w-full !py-3 text-white transition-[filter] hover:brightness-110 disabled:opacity-60"
                  style={{
                    backgroundImage: `linear-gradient(135deg, ${BRAND_TEAL} 0%, #10b981 100%)`,
                    boxShadow: `0 10px 30px -12px ${BRAND_TEAL}66`,
                  }}
                >
                  {submitting ? (
                    <>
                      <Spinner className="h-4 w-4" />
                      {mode === 'login' ? 'Signing in…' : 'Creating account…'}
                    </>
                  ) : mode === 'login' ? (
                    'Sign in'
                  ) : (
                    'Create account'
                  )}
                </button>
              </form>

              <p className="mt-6 flex items-center justify-center gap-1.5 text-[0.6875rem] text-mist-600">
                <LockIcon />
                Secured sign-in — your details stay private to NeevCloud
              </p>
            </div>
          </motion.div>
        </section>
      </div>
    </main>
  );
}

/* -------------------------------------------------------------------------- */
/* Brand panel — near-black ground with a teal glow, a rotating pitch, and a  */
/* small animated bars mark standing in for a product screenshot. Always     */
/* this fixed dark treatment (not the light/dark page tokens): it needs to   */
/* read correctly regardless of which theme the visitor's form panel is in.  */
/* -------------------------------------------------------------------------- */

const BRAND_TEAL = '#34d399';

function BrandPanel() {
  const reduce = useReducedMotion();
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    if (reduce) return;
    const id = window.setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), 5200);
    return () => window.clearInterval(id);
  }, [reduce]);

  return (
    <section
      className="relative hidden flex-col justify-between overflow-hidden bg-[#050807] p-11 lg:flex xl:p-14"
      style={{
        backgroundImage: 'radial-gradient(46rem 34rem at 50% 42%, rgb(52 211 153 / 0.1), transparent 68%)',
      }}
    >
      {/* Fine grid + soft glow for depth — pure atmosphere. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
            backgroundSize: '56px 56px',
            maskImage: 'radial-gradient(ellipse 70% 65% at 50% 40%, black 30%, transparent 78%)',
            WebkitMaskImage: 'radial-gradient(ellipse 70% 65% at 50% 40%, black 30%, transparent 78%)',
          }}
        />
      </div>

      {/* --- header --- */}
      <div className="relative flex items-center gap-3">
        <NeevCloudMark size={30} className="text-white" />
        <span className="text-lg font-bold text-white" style={{ letterSpacing: '-0.02em' }}>
          Suggestion Box
        </span>
      </div>

      {/* --- centered mark: an abstract "levels" icon standing in for a screenshot --- */}
      <div className="relative flex flex-1 items-center justify-center">
        <div aria-hidden="true" className="absolute h-56 w-56 rounded-full bg-emerald-400/12 blur-[90px]" />
        <div className="relative flex flex-col gap-3">
          {[0.62, 0.86, 0.5].map((w, i) => (
            <div key={i} className="h-2.5 w-44 overflow-hidden rounded-full bg-white/8">
              <motion.div
                className="h-full rounded-full"
                style={{ background: `linear-gradient(90deg, ${BRAND_TEAL}, #6ee7c8)` }}
                initial={false}
                animate={reduce ? { width: `${w * 100}%` } : { width: [`${w * 100}%`, `${Math.min(w * 100 + 14, 100)}%`, `${w * 100}%`] }}
                transition={{ duration: 3.4, repeat: reduce ? 0 : Infinity, ease: 'easeInOut', delay: i * 0.3 }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* --- rotating pitch --- */}
      <div className="relative">
        <div className="min-h-[8.5rem]">
          <AnimatePresence mode="wait">
            <motion.div
              key={slide}
              initial={reduce ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? undefined : { opacity: 0, y: -12 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <h2 className="text-h1 font-bold text-white text-balance">
                {SLIDES[slide].lead}
                <em className="text-[#34d399] italic">{SLIDES[slide].accent}</em>
              </h2>
              <p className="mt-3 max-w-md text-[0.9375rem] leading-relaxed text-white/60 text-pretty">
                {SLIDES[slide].body}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-6 flex items-center gap-2">
          {SLIDES.map((s, i) => (
            <button
              key={s.accent}
              type="button"
              onClick={() => setSlide(i)}
              aria-label={`Show slide ${i + 1}: ${s.lead}${s.accent}`}
              aria-current={i === slide}
              className="group p-1"
            >
              <span
                className={cx(
                  'block h-1.5 rounded-full transition-all duration-300',
                  i === slide ? 'w-7 bg-[#34d399]' : 'w-1.5 bg-white/25 group-hover:bg-white/45',
                )}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  autoComplete,
  error,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  error?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="label">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-err` : undefined}
        className="field"
      />
      {error && (
        <p id={`${id}-err`} className="mt-1.5 text-xs text-rose-300">
          {error}
        </p>
      )}
    </div>
  );
}
